import { useState, useRef, useEffect } from "react";
import { autoPopulateAddress } from "services/get-autoPopulateAddress";
import { getApartments } from "services/get-apartments";
import { addressSearch } from "services/get-addressSearch";
import { getAddressSearchPage } from "services/get-addressSearchPage";
import { postAddressSearchPage } from "services/post-addressSearchPage";
import { checkAddress } from "services/get-checkAddress";
import { Spinner, Overlay, Popover } from "react-bootstrap";
import { useRecoilState, useSetRecoilState } from "recoil";
import { addressFormState, overlayState, locationIdState, customerInfoCaptureModalState } from "atoms";
import BusinessAlertBanner from "./BusinessAlertBanner";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddressSearchModal from "components/AddressSearch/AddressSearchModal";
import { useWrapRequest, deleteAllShopCookies } from "utils";
import "components/AddressSearch/addressSearch.scss";
import { postTrackAddressNotFound } from "services/post-trackAddressNotFound";
import { AddressSearchModel, ResponseObject } from "atoms/models";

const AddressSearch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const [addressList, setAddressList] = useState([] as any);
  const [apartmentList, setApartmentList] = useState<Array<string>>([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);
  const [hoveredAddressIndex, setHoveredAddressIndex] = useState(-1);
  const [addressField, setAddressField] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [addressForm, setAddressForm] = useRecoilState(addressFormState);
  const setShowOverlay = useSetRecoilState(overlayState);
  const setLocationId = useSetRecoilState(locationIdState);
  const [showPopover, setShowPopover] = useState(false);
  const [showBusinessBanner, setShowBuisinessBanner] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showBulletedAddressBox, setShowBulletedAddressBox] = useState(false);
  const [addressesForBulletedBox, setAddressesForBulletedBox] = useState([]);
  const setCustomerInfoCaptureModal = useSetRecoilState(customerInfoCaptureModalState);
  const wrapRequest = useWrapRequest();
  const [params, setParams] = useState<any>({
    specialOfferItem: "",
    specialOfferType: "",
    specialOfferCoreType: "",
    utm_content: "",
    utm_medium: "",
    utm_source: "",
    utm_campaign: "",
  });

  // Fetch address search page on component mount, delete all cookies & and update URL search parameters
  useEffect(() => {
    const fetchAddressSearchPage = async () => {
      setShowOverlay(true);
      deleteAllShopCookies();
      sessionStorage.removeItem("item");
      sessionStorage.removeItem("type");
      sessionStorage.removeItem("utmInfo");
      try {
        const response = await getAddressSearchPage();
        // Navigating to window location if available in the response
        if (response?.["_links"]?.["ec:windowLocation"]?.href) {
          navigate(response["_links"]["ec:windowLocation"].href);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setShowOverlay(false);
      }
    };

    fetchAddressSearchPage();

    const fetchUrlParams = () => {
      const specialOfferItem = searchParams.get("item");
      const specialOfferType = searchParams.get("type");
      const specialOfferCoreType = searchParams.get("coretype");
      const utm_content = searchParams.get("utm_content");
      const utm_medium = searchParams.get("utm_medium");
      const utm_source = searchParams.get("utm_source");
      const utm_campaign = searchParams.get("utm_campaign");
      setParams({
        ...params,
        specialOfferItem: specialOfferItem,
        specialOfferType: specialOfferType,
        specialOfferCoreType: specialOfferCoreType,
        utm_content: utm_content,
        utm_medium: utm_medium,
        utm_source: utm_source,
        utm_campaign: utm_campaign,
      });
      if (specialOfferItem && specialOfferType) {
        sessionStorage.setItem("item", specialOfferItem);
        sessionStorage.setItem("type", specialOfferType);
      }
      if (utm_content && utm_medium && utm_source && utm_campaign) {
        sessionStorage.setItem(
          "utmInfo",
          JSON.stringify({
            content: utm_content,
            medium: utm_medium,
            source: utm_source,
            campaign: utm_campaign,
          })
        );
      }
    };
    fetchUrlParams();

    if (inputRef.current) {
      inputRef.current.focus();
    }
    window.scroll(0, 0);
    // eslint-disable-next-line
  }, [navigate, setShowOverlay, searchParams]);

  useEffect(() => {
    setCustomerInfoCaptureModal(false);
    // eslint-disable-next-line
  }, []);

  // Handle input change for address field
  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setSpinner(true);
    const { value } = event.target;
    setAddressField(value);
    const response: ResponseObject = await autoPopulateAddress(value);

    if (response) {
      setSpinner(false);
    }

    setAddressList([]);
    setAddressList(response);
    setSelectedAddressIndex(0);

    const match = value.match(/^(\d+)\s*(.*)$/);
    let addressNumber: string = "";
    let restOfAddress: string = "";
    if (match) {
      addressNumber = match[1];
      restOfAddress = match[2];
    }
    setAddressForm({
      ...addressForm,
      streetNumber: addressNumber,
      streetName: restOfAddress,
    });
  };

  // Fetch apartments based on street address and zip code
  const fetchApartments = async (streetAddress: string, zipCode: string) => {
    const response: Array<string> = await getApartments(streetAddress, zipCode);
    response.unshift("");
    setApartmentList(response);
  };

  // Handle key down events for address field
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setHoveredAddressIndex(-1);
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedAddressIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedAddressIndex((prevIndex) => Math.min(prevIndex + 1, addressList.length - 1));
    } else if (event.key === "Enter") {
      submitRef.current?.focus();
      if (selectedAddressIndex !== -1 && addressList[selectedAddressIndex]) {
        const { streetNumber, streetName, zipCode, apartmentNumber, city, stateProv, locationId } = addressList[selectedAddressIndex];
        setAddressField(`${streetNumber} ${streetName}`);
        setAddressList([]);
        setAddressForm({
          ...addressForm,
          streetName: streetName,
          streetNumber: streetNumber,
          city: city,
          stateProv: stateProv,
          zipCode: zipCode,
          apartmentNumber: `${apartmentNumber}`,
          locationId: locationId,
        });
        if (apartmentNumber) {
          fetchApartments(`${streetNumber} ${streetName}`, `${zipCode}`);
        }
      }
    }
  };

  // Handle click on address item in the list
  const handleAddressSelectClick = (address: AddressSearchModel) => {
    const { streetNumber, streetName, city, stateProv, zipCode, apartmentNumber, locationId } = address;
    setAddressList([]);
    if (apartmentNumber) {
      fetchApartments(`${streetNumber} ${streetName}`, `${zipCode}`);
    }
    setAddressField(`${streetNumber} ${streetName}`);
    setAddressForm({
      ...addressForm,
      streetName: streetName,
      streetNumber: streetNumber,
      city: city,
      stateProv: stateProv,
      zipCode: zipCode,
      apartmentNumber: `${apartmentNumber}`,
      locationId: locationId,
    });
    submitRef.current?.focus();
  };

  // Handle mouse enter on address item
  const handleMouseEnter = (index: number) => {
    setHoveredAddressIndex(index);
  };

  // Handle mouse leave from address item
  const handleMouseLeave = () => {
    setHoveredAddressIndex(-1);
  };

  // Handle selection of apartment from dropdown
  const handleApartmentSelect = async (value: string) => {
    const newAddressForm: AddressSearchModel = { ...addressForm, apartmentNumber: value };
    const addressresponse: Array<AddressSearchModel> = await addressSearch(
      newAddressForm.streetNumber,
      newAddressForm.streetName,
      newAddressForm.apartmentNumber,
      newAddressForm.city,
      newAddressForm.stateProv,
      newAddressForm.zipCode
    );
    setAddressForm(addressresponse[0]);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    wrapRequest(async () => {
      // If user hasn't selected an address and clicks submit show the bulleted list box with addresses then return.
      if (
        !addressForm.streetNumber &&
        !addressForm.streetName &&
        !addressForm.city &&
        !addressForm.stateProv &&
        !addressForm.zipCode &&
        addressForm.locationId === 0 &&
        addressList.length > 0
      ) {
        setShowBulletedAddressBox(true);
        setAddressesForBulletedBox(addressList);
        setAddressList([]);
        return;
      }

      if (!addressForm.streetNumber || !addressForm.streetName || !addressForm.city || !addressForm.stateProv || !addressForm.zipCode) {
        // If any part of the address is missing show the user the popover error box
        postTrackAddressNotFound(addressField);
        setShowPopover(true);
        setAddressField("");
        setSpinner(false);
        return;
      }

      // Make a call to get the address data that the user has selected
      const response = await addressSearch(
        addressForm.streetNumber,
        addressForm.streetName,
        addressForm.apartmentNumber,
        addressForm.city,
        addressForm.stateProv,
        addressForm.zipCode
      );

      if (!response.length) {
        postTrackAddressNotFound(addressField);
        // If nothing comes back in the response show the error popover
        setShowPopover(true);
      } else {
        // Process the successful request
        await handleAddressResponse(response[0]);
      }
    });
  };

  const handleAddressResponse = async (addressData: AddressSearchModel) => {
    // Call API to check address using locationId from addressData
    const checkAddressResponse: any = await checkAddress(addressData.locationId);
    // Destructure response object, set default values for _links, residentialStructure, and hasExistingService
    const { _links: links = {}, residentialStructure, hasExistingService } = checkAddressResponse;

    // Check if the address is not residential
    if (!residentialStructure) {
      // If not residential, show business banner and return
      setShowBuisinessBanner(true);
      return;
    }

    // Check if there is an existing service for the address
    if (hasExistingService) {
      // If there is an existing service, set locationId and show address modal, then return
      setLocationId(addressForm.locationId);
      setShowAddressModal(true);
      return;
    }

    let windowLocationHref;

    // Extract window location href from response links if available
    if (links["ec:windowLocation"]) {
      windowLocationHref = links["ec:windowLocation"].href;
    }

    let postAddressResponse;

    // Navigate to window location href if available, otherwise, post address search page and navigate
    if (windowLocationHref) {
      navigate(windowLocationHref);
    } else {
      // If special offer data is available, include it in the post address search request
      const { specialOfferItem, specialOfferType } = params;
      postAddressResponse = await postAddressSearchPage(
        addressForm.locationId,
        null,
        specialOfferItem,
        specialOfferType,
        null,
        null,
        null,
        null,
        null
      );

      const redirectUrl = postAddressResponse?.["_links"]?.["ec:windowLocation"]?.href;
      // Will look for leadInfoPage in the response to display the Lead Capture Form.
      if (redirectUrl?.includes("leadInfoPage")) {
        setCustomerInfoCaptureModal(true);
        return;
      } else {
        navigate(postAddressResponse._links["ec:windowLocation"].href);
      }
    }
  };

  const highlightMatch = (combined: string, query: string) => {
    const index = combined.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) {
      return combined;
    }
    return (
      <>
        {combined.substring(0, index)}
        <strong>{combined.substring(index, index + query.length)}</strong>
        {combined.substring(index + query.length)}
      </>
    );
  };

  return (
    <div>
      <p>
        <strong>To see offers in your area, enter your address and then select your address from the dropdown menu when it appears.</strong>
      </p>
      <div className="address-search-container">
        <div className="row address-search-inner-container">
          <div className={addressForm.zipCode ? "col-md-6 col-sm-12" : "col-md-12 col-sm-12"}>
            <div className="form-group">
              <label className="form-label">Street Address</label>
              {spinner ? <Spinner animation="border" /> : null}
              <input
                type="text"
                id="addressInput"
                autoComplete="off"
                ref={inputRef}
                value={addressField ? addressField : ""}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className="form-control address-input-field"
                onBlur={() => setSpinner(false)}
                onFocus={() => {
                  setAddressList([]);
                  setApartmentList([]);
                  setAddressField("");
                  setAddressForm({
                    ...addressForm,
                    streetNumber: "",
                    streetName: "",
                    city: "",
                    stateProv: "",
                    zipCode: "",
                    apartmentNumber: "",
                    locationId: 0,
                  });
                  setShowPopover(false);
                  setShowBuisinessBanner(false);
                  setShowBulletedAddressBox(false);
                  setAddressesForBulletedBox([]);
                }}
              />
              <Overlay show={showPopover} target={() => document.querySelector("#addressInput")} placement="bottom">
                <Popover id="popover-contained" className="address-serch-popover">
                  <Popover.Header>Oops! Something went wrong.</Popover.Header>
                  <Popover.Body>
                    <p>
                      We can’t seem to locate your address in our system. Please contact us via chat or call us{" "}
                      <a href="tel:888-403-2667">(888) 403-2667</a>
                    </p>
                  </Popover.Body>
                </Popover>
              </Overlay>
            </div>
          </div>
          {apartmentList.length > 0 ? (
            <div className="col-md-2 col-sm-12">
              <div className="form-group apartment-container">
                <label className="mb-2">Apartment #</label>
                <select
                  style={
                    apartmentList.length > 0 && addressForm.apartmentNumber === "0"
                      ? {
                          overflow: "scroll",
                          height: "100%",
                          maxHeight: "220px",
                          paddingBottom: "15px",
                        }
                      : {}
                  }
                  className="form-select"
                  value={addressForm?.apartmentNumber ? addressForm.apartmentNumber : ""}
                  size={apartmentList.length > 0 && addressForm.apartmentNumber === "0" ? apartmentList.length : 0}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    handleApartmentSelect(e.target.value);
                  }}
                >
                  {apartmentList.map((apartment: string) => {
                    return (
                      <option key={apartment} value={apartment}>
                        {apartment}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          ) : null}
          {addressForm.zipCode ? (
            <div className="col-md-3 col-sm-12">
              <div className="form-group me-1 text-start zip-code-container">
                <label className="form-label">Zip Code</label>
                <input type="text" className="form-control zip-code" disabled value={addressForm.zipCode} />
              </div>
            </div>
          ) : null}
        </div>
        {showBulletedAddressBox ? (
          <div className="mt-3">
            <div className="card">
              <div className="card-header" style={{ background: "steelblue", color: "#ffffff" }}>
                Pick from Address List
              </div>
              <div className="p-3">
                {addressesForBulletedBox.length > 0
                  ? addressesForBulletedBox.map((address: AddressSearchModel, index: number) => {
                      return (
                        <div key={index} className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="bulletedAddressList"
                            id={`bulletedAddressList${index}`}
                            value={`address${index}`}
                            onClick={() => {
                              handleAddressSelectClick(address);
                              setShowBulletedAddressBox(false);
                            }}
                          />
                          <label className="form-check-label" htmlFor={`bulletedAddressList${index}`}>
                            {address.streetNumber}&nbsp;
                            {address.streetName},&nbsp;
                            {address.city}&nbsp;
                            {address.stateProv}&nbsp;
                            {address.zipCode}&nbsp;
                          </label>
                        </div>
                      );
                    })
                  : null}
              </div>
            </div>
          </div>
        ) : null}
        <ul className="list-group">
          {!addressList || addressList.length === 0
            ? null
            : addressList.map((address: AddressSearchModel, index: number) => {
                const combined = `${address.streetNumber} ${address.streetName}`;
                return (
                  <li
                    key={index}
                    className={`list-group-item ${index === selectedAddressIndex ? "selected" : ""} ${
                      index === hoveredAddressIndex ? "selected" : ""
                    }`}
                    onClick={() => handleAddressSelectClick(address)}
                    onMouseEnter={() => {
                      handleMouseEnter(index);
                      setSelectedAddressIndex(-1);
                    }}
                    onMouseLeave={handleMouseLeave}
                  >
                    {highlightMatch(combined, addressField)},&nbsp;
                    {address.city}&nbsp;
                    {address.stateProv}&nbsp;
                    {address.zipCode}&nbsp;
                    {address.apartmentNumber === "0" ? <strong>(Choose Apt. #)</strong> : null}
                  </li>
                );
              })}
        </ul>
        {showBusinessBanner ? <BusinessAlertBanner setShowBuisinessBanner={setShowBuisinessBanner} /> : null}
        <button
          ref={submitRef}
          className="btn btn-gold-submit btn-primary"
          type="submit"
          disabled={(apartmentList.length > 0 && addressForm.apartmentNumber === "0") || (addressForm.zipCode === "" && addressField.length < 7)}
          onClick={handleSubmit}
        >
          Submit
        </button>
      </div>
      <AddressSearchModal showAddressModal={showAddressModal} setShowAddressModal={setShowAddressModal} locationId={addressForm.locationId} />
    </div>
  );
};

export default AddressSearch;
